import { useState, useCallback } from 'react';
import { getCategories } from '../services/api';

// Map category IDs to specialized form types
const CATEGORY_TYPE_MAPPING = {
  // Electronics - specific device subcategories
  12: 'electronics', // Computers & Laptops
  19: 'electronics', // Mobile Phones
  20: 'electronics', // Tablets
  22: 'electronics', // Smartwatches
  13: 'electronics', // TVs & Audio Systems
  14: 'electronics', // Cameras & Drones
  15: 'electronics', // Gaming // consoles
  
  // Services - all service subcategories
  85: 'services', // Repair & Maintenance
  86: 'services', // Home Cleaning & Laundry
  87: 'services', // Pest Control
  88: 'services', // Event Planning & Photography
  89: 'services', // Health & Fitness
  90: 'services', // Education & Tutoring
  91: 'services', // Logistics & Delivery
  92: 'services', // Business & Tech Services
  93: 'services', // Legal & Financial Services
  94: 'services', // Plumbing & Water Treatment
  
  // Vehicles - only complete vehicles
  24: 'vehicles', // Cars
  25: 'vehicles', // Motorcycles & Scooters
  26: 'vehicles', // Trucks & Trailers
  27: 'vehicles', // Buses & Vans
  30: 'vehicles', // Boats & Watercraft
  
  // Properties - all property subcategories
  62: 'properties', // Houses for Sale
  63: 'properties', // Houses for Rent
  64: 'properties', // Land & Plots
  65: 'properties', // Commercial Property
  66: 'properties', // Short Let Apartments
  68: 'properties', // Office & Shop Spaces
  69: 'properties', // Hostels & Student Accommodation
};

const getCategoryType = (categoryId, allCategories) => {
  if (!categoryId) return null;
  
  const numericId = parseInt(categoryId);
  // console.log(`🔍 Checking category type for ID: ${numericId}`);
  
  // Direct mapping by category ID
  const directType = CATEGORY_TYPE_MAPPING[numericId];
  if (directType) {
    const category = allCategories.find(cat => cat.id === numericId);
    // console.log(`🎯 Direct mapping found: ${directType} for "${category?.name}"`);
    return directType;
  }
  
  // If no direct mapping, check if it's a main category (should not show specialized form)
  const category = allCategories.find(cat => cat.id === numericId);
  if (category) {
    if (category.parent_id === null) {
      // console.log(`🚫 Main category "${category.name}" - no specialized form`);
      return null;
    }
    // console.log(`ℹ️ No specialized form for subcategory: "${category.name}"`);
  }
  
  return null;
};

export const useCategoryData = () => {
  const [allCategories, setAllCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [categoryType, setCategoryType] = useState(null);

  const loadCategories = useCallback(async () => {
    try {
      // console.log('📥 Loading categories...');
      const categoriesResponse = await getCategories();
      const categoriesData = categoriesResponse.data || [];
      
      // console.log(`✅ Loaded ${categoriesData.length} categories`);
      setAllCategories(categoriesData);
      
      const mainCats = categoriesData.filter(cat => cat.parent_id === null);
      // console.log(`📊 Found ${mainCats.length} main categories`);
      setMainCategories(mainCats);
    } catch (err) {
      // console.error('❌ Error fetching categories:', err);
      throw err;
    }
  }, []);

  const updateSubcategories = useCallback((categoryId, allCats) => {
    // console.log(`🔄 Updating subcategories for category ID: ${categoryId}`);
    
    if (categoryId) {
      const subs = allCats.filter(cat => cat.parent_id === parseInt(categoryId));
      // console.log(`📋 Found ${subs.length} subcategories`);
      setSubcategories(subs);
      
      // For main categories, don't set category type (wait for subcategory selection)
      const category = allCats.find(cat => cat.id === parseInt(categoryId));
      if (category && category.parent_id === null) {
        // console.log(`🚫 Main category selected - clearing category type`);
        setCategoryType(null);
      }
    } else {
      // console.log('🔄 Clearing subcategories and category type');
      setSubcategories([]);
      setCategoryType(null);
    }
  }, []);

  // New function to update category type when subcategory is selected
  const updateCategoryType = useCallback((subcategoryId, allCats) => {
    // console.log(`🎯 Updating category type for subcategory ID: ${subcategoryId}`);
    
    if (subcategoryId) {
      const detectedType = getCategoryType(subcategoryId, allCats);
      // console.log(`🔧 Detected category type: ${detectedType}`);
      setCategoryType(detectedType);
    } else {
      // console.log('🔄 Clearing category type (no subcategory selected)');
      setCategoryType(null);
    }
  }, []);

  return {
    allCategories,
    mainCategories,
    subcategories,
    categoryType,
    loadCategories,
    updateSubcategories,
    updateCategoryType
  };
};